import { Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { InMemoryDbService, RequestInfo, getStatusText, STATUS } from 'angular-in-memory-web-api';
import { Observable, throwError } from 'rxjs';
import { PagedResult } from '../models/paged-result';
import { GetOccurrenceReviews } from '../../features/occurrence-review/in-memory-data/occurrence-review-data';
import { GetOccurrenceHistories } from '../../features/occurrence-history/in-memory-data/occurrence-history-data';
import {
  OccurrenceFieldChange,
  OccurrenceHistoryEntry,
} from '../../features/occurrence-history/models/occurrence-history';
import { OccurrenceReview, OfficeIrgReview } from '../../features/occurrence-review/models/occurrence-review';
import { GetRailTransportOperators } from '../../features/my-favourite/in-memory-data/rail-transport-operators-data';
import { GetPrioritisationConfig } from '../../features/manage-prioritisation/in-memory-data/prioritisation-config-data';
import { GetRailSafetyOfficers } from '../../features/assign-rso-office/in-memory-data/assign-rso-office-data';
import { AuthService } from '@onrsr/ui/auth';
import { AccessControlDb, GetAccessControl } from '../../features/access-control/in-memory-data/access-control-data';

const OCCURRENCE_TRACKED_FIELDS = ['escalatedToIrg'] as const;

const OFFICE_REVIEW_TRACKED_FIELDS = [
  'rsoReview',
  'rsoReviewedBy',
  'rsoReviewDateTime',
  'rsoReviewNotes',
  'escalateToIrg',
  'escalateToIrgBy',
  'escalateToIrgDateTime',
  'escalateToIrgComments',
  'severity',
  'regulatoryValue',
  'priority',
  'prioritisedFollowUp',
  'relatedActivities',
  'seriousOccurrence',
  'irgComments',
  'leadRso',
  'irgStatus',
] as const satisfies ReadonlyArray<keyof OfficeIrgReview>;

// Mock API. Each collection is served as GET api/v1.0/<name> returning a
// PagedResult<T> envelope, and PUT api/v1.0/<name>/<Id> merging the body into
// the matching record. Filtering/sorting/searching is done client-side by the
// feature services, so the backend contract is plain paged CRUD per collection.
@Injectable({
  providedIn: 'root',
})
export class InMemoryDataService implements InMemoryDbService {
  // Resolved lazily: AuthService -> NGXLogger -> HttpBackend -> this service would otherwise be a DI cycle.
  private readonly injector = inject(Injector);

  // Demo: PUT me/session { expiresInSeconds } schedules rejection of the bearer token in use, as if the
  // API-side session had ended. A token obtained by signing in again starts a new session.
  private expiredToken: string | null = null;
  private expiresAt = Infinity;

  createDb() {
    return {
      occurrenceReviews: GetOccurrenceReviews.occurrenceReviews,
      occurrenceHistories: GetOccurrenceHistories.entries,
      railTransportOperators: GetRailTransportOperators.railTransportOperators,
      prioritisationConfig: GetPrioritisationConfig.prioritisationConfig,
      railSafetyOfficers: GetRailSafetyOfficers.railSafetyOfficers,
      permissions: GetAccessControl.permissions,
      roles: GetAccessControl.roles,
      users: GetAccessControl.users,
    };
  }

  get(reqInfo: RequestInfo): Observable<unknown> | undefined {
    if (this.sessionExpired(reqInfo)) {
      return this.unauthorised(reqInfo);
    }
    if (reqInfo.collectionName === 'me') {
      return this.getMe(reqInfo);
    }

    if (!reqInfo.collection) {
      return undefined;
    }

    if (reqInfo.collectionName === 'occurrenceHistories') {
      return this.getOccurrenceHistories(reqInfo);
    }

    const collection = reqInfo.collection as Array<Record<string, unknown>>;

    if (reqInfo.id != null) {
      const item = collection.find(
        (entry) =>
          String(entry['id'] ?? entry['Id']) === String(reqInfo.id),
      );
      if (!item) {
        return reqInfo.utils.createResponse$(() => ({
          body: { error: `Item with id ${reqInfo.id} not found` },
          status: STATUS.NOT_FOUND,
          statusText: getStatusText(STATUS.NOT_FOUND),
        }));
      }

      return reqInfo.utils.createResponse$(() => ({
        body: item,
        status: STATUS.OK,
        statusText: getStatusText(STATUS.OK),
      }));
    }

    const query = reqInfo.query;
    const pageNumber = this.readNumberParam(query, 'pageNumber', 1);
    const pageSize = this.readNumberParam(query, 'pageSize', 20);

    const totalItems = collection.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const startIndex = (pageNumber - 1) * pageSize;

    const pagedResult: PagedResult<unknown> = {
      items: collection.slice(startIndex, startIndex + pageSize),
      totalItems,
      pageNumber,
      pageSize,
      totalPages,
      hasPreviousPage: pageNumber > 1,
      hasNextPage: pageNumber < totalPages,
    };

    return reqInfo.utils.createResponse$(() => ({
      body: pagedResult,
      status: STATUS.OK,
      statusText: getStatusText(STATUS.OK),
    }));
  }

  post(reqInfo: RequestInfo): Observable<unknown> | undefined {
    return this.sessionExpired(reqInfo) ? this.unauthorised(reqInfo) : undefined;
  }

  put(reqInfo: RequestInfo): Observable<unknown> | undefined {
    if (this.sessionExpired(reqInfo)) {
      return this.unauthorised(reqInfo);
    }
    if (reqInfo.collectionName === 'me' && reqInfo.id === 'session') {
      return this.putSession(reqInfo);
    }
    if (reqInfo.collectionName === 'occurrenceReviews') {
      return this.putOccurrenceReview(reqInfo);
    }

    const collection = reqInfo.collection as Array<Record<string, unknown>>;
    const body = reqInfo.utils.getJsonBody(reqInfo.req) as Record<string, unknown>;
    const id = reqInfo.id ?? body['id'] ?? body['Id'];
    const index = collection.findIndex((item) => String(item['id'] ?? item['Id']) === String(id));

    if (index === -1) {
      return reqInfo.utils.createResponse$(() => ({
        body: { error: `Item with id ${id} not found` },
        status: STATUS.NOT_FOUND,
        statusText: getStatusText(STATUS.NOT_FOUND),
      }));
    }

    collection[index] = { ...collection[index], ...body };

    return reqInfo.utils.createResponse$(() => ({
      body: collection[index],
      status: STATUS.OK,
      statusText: getStatusText(STATUS.OK),
    }));
  }

  delete(reqInfo: RequestInfo): Observable<unknown> | undefined {
    if (this.sessionExpired(reqInfo)) {
      return this.unauthorised(reqInfo);
    }
    if (reqInfo.collectionName !== 'roles') {
      return undefined;
    }

    const db = reqInfo.utils.getDb() as AccessControlDb;
    const roleId = Number(reqInfo.id);
    const role = db.roles.find((candidate) => candidate.id === roleId);
    if (!role) {
      return this.respond(reqInfo, { error: `Role ${roleId} not found` }, STATUS.NOT_FOUND);
    }
    if (role.isSystem) {
      return this.respond(reqInfo, { error: 'System roles cannot be deleted' }, STATUS.CONFLICT);
    }
    const assignedUsers = db.users.filter((user) => user.roleIds.includes(roleId)).length;
    if (assignedUsers > 0) {
      return this.respond(reqInfo, { error: `Role is assigned to ${assignedUsers} users` }, STATUS.CONFLICT);
    }

    db.roles.splice(db.roles.indexOf(role), 1);
    return this.respond(reqInfo, undefined, STATUS.NO_CONTENT);
  }

  private sessionExpired(reqInfo: RequestInfo): boolean {
    return Date.now() >= this.expiresAt && this.bearerToken(reqInfo) === this.expiredToken;
  }

  private unauthorised(reqInfo: RequestInfo): Observable<unknown> {
    return this.respond(reqInfo, { error: 'Session expired' }, STATUS.UNAUTHORIZED);
  }

  private putSession(reqInfo: RequestInfo): Observable<unknown> {
    const { expiresInSeconds = 0 } = reqInfo.utils.getJsonBody(reqInfo.req) as { expiresInSeconds?: number };
    this.expiredToken = this.bearerToken(reqInfo);
    this.expiresAt = Date.now() + expiresInSeconds * 1000;
    return this.respond(reqInfo, { expiresAt: new Date(this.expiresAt).toISOString() }, STATUS.OK);
  }

  private bearerToken(reqInfo: RequestInfo): string | null {
    return (reqInfo.req as HttpRequest<unknown>).headers.get('Authorization');
  }

  // GET me: the caller is resolved from the auth context, as a real API would from the bearer token.
  private getMe(reqInfo: RequestInfo): Observable<unknown> {
    const db = reqInfo.utils.getDb() as AccessControlDb;
    const user = GetAccessControl.resolveUser(db, this.injector.get(AuthService).currentUser());
    return this.respond(reqInfo, GetAccessControl.buildSession(db, user), STATUS.OK);
  }

  // Error statuses are raised as HttpErrorResponse, as HttpXhrBackend does; the in-memory backend would
  // otherwise emit a plain options object that interceptors cannot recognise.
  private respond(reqInfo: RequestInfo, body: object | undefined, status: number): Observable<unknown> {
    const statusText = getStatusText(status);
    if (status >= 400) {
      return throwError(() => new HttpErrorResponse({ error: body, status, statusText, url: reqInfo.url }));
    }
    return reqInfo.utils.createResponse$(() => ({ body, status, statusText }));
  }

  private putOccurrenceReview(reqInfo: RequestInfo): Observable<unknown> | undefined {
    const occurrences = reqInfo.collection as OccurrenceReview[];
    const body = reqInfo.utils.getJsonBody(reqInfo.req) as Partial<OccurrenceReview> & {
      changedBy?: string;
      historySource?: 'Occurrence Review' | 'IRG';
    };
    const id = Number(reqInfo.id ?? body.id);
    const index = occurrences.findIndex((occurrence) => occurrence.id === id);

    if (index === -1) {
      return reqInfo.utils.createResponse$(() => ({
        body: { error: `Occurrence review ${id} not found` },
        status: STATUS.NOT_FOUND,
        statusText: getStatusText(STATUS.NOT_FOUND),
      }));
    }

    const existing = occurrences[index];
    const changes: OccurrenceFieldChange[] = [];

    for (const field of OCCURRENCE_TRACKED_FIELDS) {
      const previousValue = this.formatHistoryValue(existing[field]);
      const newValue = this.formatHistoryValue(body[field] ?? existing[field]);
      if (previousValue !== newValue) {
        changes.push({ field, previousValue, newValue });
      }
    }

    const officeReviewChanges = this.diffOfficeReviews(existing.irgReview, body.irgReview ?? existing.irgReview);
    changes.push(...officeReviewChanges.changes);

    if (changes.length === 0) {
      return reqInfo.utils.createResponse$(() => ({
        body: existing,
        status: STATUS.OK,
        statusText: getStatusText(STATUS.OK),
      }));
    }

    const updated: OccurrenceReview = {
      ...existing,
      ...body,
    };
    delete (updated as OccurrenceReview & { changedBy?: string; historySource?: string }).changedBy;
    delete (updated as OccurrenceReview & { changedBy?: string; historySource?: string }).historySource;
    occurrences[index] = updated;

    GetOccurrenceHistories.addEntry({
      occurrenceId: id,
      operatorReference: updated.operatorReference,
      amalgamatedId: updated.amalgamatedId,
      changedBy: body.changedBy ?? 'Unknown User',
      changedAt: new Date().toISOString(),
      changes,
      source: body.historySource ?? 'Occurrence Review',
      office: officeReviewChanges.office ?? this.resolveHistoryOffice(existing, updated, changes),
    });

    return reqInfo.utils.createResponse$(() => ({
      body: updated,
      status: STATUS.OK,
      statusText: getStatusText(STATUS.OK),
    }));
  }

  private getOccurrenceHistories(reqInfo: RequestInfo): Observable<unknown> {
    const collection = reqInfo.collection as OccurrenceHistoryEntry[];
    const query = reqInfo.query;

    const pageNumber = this.readNumberParam(query, 'pageNumber', 1);
    const pageSize = this.readNumberParam(query, 'pageSize', 20);
    const searchText = this.readStringParam(query, 'searchText').trim().toLowerCase();
    const sortProperty = this.readStringParam(query, 'sortProperty', 'changedAt');
    const sortDirection = this.readStringParam(query, 'sortDirection', 'descending');
    const occurrenceId = this.readNumberParam(query, 'occurrenceId', Number.NaN);

    let filtered = collection;

    if (!Number.isNaN(occurrenceId)) {
      filtered = filtered.filter((entry) => entry.occurrenceId === occurrenceId);
    }

    if (searchText) {
      filtered = filtered.filter((entry) =>
        [
          entry.operatorReference,
          entry.amalgamatedId,
          entry.changedBy,
          entry.office ?? '',
          entry.source ?? '',
          ...entry.changes.flatMap((change) => [
            change.field,
            change.previousValue,
            change.newValue,
          ]),
        ].some((value) => value.toString().toLowerCase().includes(searchText)),
      );
    }

    if (sortProperty) {
      const direction = sortDirection === 'descending' ? -1 : 1;
      filtered = [...filtered].sort((a, b) => {
        const aValue = this.historySortValue(a, sortProperty);
        const bValue = this.historySortValue(b, sortProperty);
        return String(aValue).localeCompare(String(bValue), undefined, { numeric: true }) * direction;
      });
    }

    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const startIndex = (pageNumber - 1) * pageSize;

    const pagedResult: PagedResult<OccurrenceHistoryEntry> = {
      items: filtered.slice(startIndex, startIndex + pageSize),
      totalItems,
      pageNumber,
      pageSize,
      totalPages,
      hasPreviousPage: pageNumber > 1,
      hasNextPage: pageNumber < totalPages,
    };

    return reqInfo.utils.createResponse$(() => ({
      body: pagedResult,
      status: STATUS.OK,
      statusText: getStatusText(STATUS.OK),
    }));
  }

  private historySortValue(entry: OccurrenceHistoryEntry, sortProperty: string): string | number {
    switch (sortProperty) {
      case 'occurrenceId':
        return entry.occurrenceId;
      case 'operatorReference':
        return entry.operatorReference;
      case 'amalgamatedId':
        return entry.amalgamatedId;
      case 'changedBy':
        return entry.changedBy;
      case 'changedAt':
        return entry.changedAt;
      case 'office':
        return entry.office ?? '';
      case 'source':
        return entry.source ?? '';
      default:
        return '';
    }
  }

  private diffOfficeReviews(
    previousReviews: OfficeIrgReview[],
    nextReviews: OfficeIrgReview[],
  ): { changes: OccurrenceFieldChange[]; office: string | null } {
    const changes: OccurrenceFieldChange[] = [];
    let changedOffice: string | null = null;
    const previousByOffice = new Map(previousReviews.map((review) => [review.office, review]));

    for (const nextReview of nextReviews) {
      const previousReview = previousByOffice.get(nextReview.office);
      for (const field of OFFICE_REVIEW_TRACKED_FIELDS) {
        const previousValue = this.formatHistoryValue(previousReview?.[field]);
        const newValue = this.formatHistoryValue(nextReview[field]);
        if (previousValue !== newValue) {
          changes.push({ field, previousValue, newValue });
          changedOffice = nextReview.office;
        }
      }
    }

    return { changes, office: changedOffice };
  }

  private resolveHistoryOffice(
    _existing: OccurrenceReview,
    updated: OccurrenceReview,
    changes: OccurrenceFieldChange[],
  ): string | null {
    if (changes.length === 0) {
      return null;
    }
    return updated.irgReview[0]?.office ?? null;
  }

  private formatHistoryValue(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      return JSON.stringify(value);
    }
    return String(value);
  }

  private readStringParam(
    query: Map<string, string[]> | undefined,
    name: string,
    defaultValue = '',
  ): string {
    const value = query?.get(name)?.[0];
    return value ?? defaultValue;
  }

  private readNumberParam(
    query: Map<string, string[]> | undefined,
    name: string,
    defaultValue: number,
  ): number {
    const value = parseInt(query?.get(name)?.[0] ?? '', 10);
    return Number.isNaN(value) ? defaultValue : value;
  }
}
